import * as React from "react";
import { cn } from "../../lib/cn";

export interface SurfaceLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Header slot — usually a `<SurfaceHeader>`. Optional: surfaces can also
   * render the header inline at the top of `children` for richer layouts
   * (e.g. sticky header above scrollable content).
   */
  header?: React.ReactNode;
  /**
   * Companion Hero slot — `<CompanionHero>` proactive nudge. Sits between
   * the header and the toolbar; surfaces can omit it (idle state).
   */
  hero?: React.ReactNode;
  /**
   * Sub-tabs / filter chips strip. Rendered above the content area.
   */
  toolbar?: React.ReactNode;
  /** Main content area — list, grid, timeline, dashboard. */
  children?: React.ReactNode;
  /** Accessibility label for the scrollable content region. */
  contentAriaLabel?: string;
  /**
   * Ref to the SCROLLABLE content ``<section>`` (not the outer shell — that's
   * the forwarded ``ref``). Additive: virtualized hubs (e.g. Documenti) pass
   * this so a windowing library can use it as the scroll element. Every other
   * hub omits it and is unaffected.
   */
  contentRef?: React.Ref<HTMLElement>;
  /**
   * Override classes for the content region. Defaults to the scrollable
   * `px-8 pt-4 pb-10` list padding (the `pt-4` gives the first band breathing
   * room below the command-bar divider); full-height detail bodies (e.g. a two-column
   * console that manages its own scroll) can opt out of the default scroll +
   * padding without reaching for `.surface` styles directly.
   */
  contentClassName?: string;
}

/**
 * SurfaceLayout — single source-of-truth chrome for every workspace tab.
 *
 * Mirrors the v18 mockup `.surface` shell:
 *
 *   ┌────────────────────────────────────────────┐
 *   │ SurfaceHeader (label / title / meta · CTA) │  ← always present
 *   ├────────────────────────────────────────────┤
 *   │ CompanionHero (proactive AI nudge)         │  ← optional
 *   ├────────────────────────────────────────────┤
 *   │ Toolbar (filter chips · search · actions)  │  ← optional
 *   ├────────────────────────────────────────────┤
 *   │ Content (scrollable list / grid / chart)   │  ← children
 *   └────────────────────────────────────────────┘
 *
 * Pattern intentionally DRY — every Hub composes these slots, none of the
 * surface-specific UIs reach for `.surface` styles directly.
 */
export const SurfaceLayout = React.forwardRef<HTMLDivElement, SurfaceLayoutProps>(
  (
    {
      header,
      hero,
      toolbar,
      children,
      contentAriaLabel,
      contentClassName,
      contentRef,
      className,
      ...rest
    },
    ref,
  ) => (
    <section
      ref={ref}
      className={cn(
        // Transparent — the workspace pane parent already supplies the
        // semi-translucent white so the brand blobs show through.
        "flex h-full w-full animate-surface-in flex-col motion-reduce:animate-none",
        className,
      )}
      data-ando-surface=""
      {...rest}
    >
      {header}
      {/* Jul 2026 — tighter hero band (pt-5 → pt-3) so the proactive nudge
          costs less vertical room above the toolbar on every hub. */}
      {hero ? <div className="px-8 pt-3">{hero}</div> : null}
      {toolbar}
      <section
        ref={contentRef}
        aria-label={contentAriaLabel}
        className={cn("min-h-0 flex-1 overflow-y-auto px-8 pt-4 pb-10", contentClassName)}
      >
        {children}
      </section>
    </section>
  ),
);
SurfaceLayout.displayName = "SurfaceLayout";
